import {
  CATEGORY_DEFAULTS,
  type Category,
  dec,
  formatMoney,
  formatQuantity,
  formatSpec,
  formatUnitCost,
  HOP_FORM_LABELS,
  MOVEMENT_LABELS,
  type MovementKind,
  type Pack,
  plural,
  readsWithMaker,
  type SpecValue,
  sameSize,
  UNITS,
  type UnitId,
  YEAST_FORM_DEFAULTS,
  ZERO,
} from "@bl/domain";
import type { Item, Movement, PackOption, Spec } from "./api";

/** Matches the API's marker for a value a person set; one account for now. */
export const USER_SOURCE = "User";

import type { ColorUnit } from "./layout/layout";

export const categoryLabel = (c: string) =>
  c in CATEGORY_DEFAULTS ? CATEGORY_DEFAULTS[c as Category].label : c;

export const unitLabel = (u: string) => (u in UNITS ? UNITS[u as UnitId].label : u);

/** The form an item is in, where its kind has forms: a hop's "T-90" or "Cryo", a yeast's "Dry". */
export function formLabel(item: Pick<Item, "hopForm" | "yeastForm">): string {
  if (item.hopForm) return HOP_FORM_LABELS[item.hopForm];
  if (item.yeastForm) return YEAST_FORM_DEFAULTS[item.yeastForm].label;
  return "";
}

/**
 * An item's region of its own. A hop has none: one item holds its variety and form from every region,
 * and each lot says its own (Scott, 2026-10-02). A hop added from the catalog before then still carries
 * its product's, which is not shown.
 */
export const regionOf = (item: Pick<Item, "category" | "origin">) =>
  item.category === "hop" ? null : item.origin;

/**
 * Who made it, as Inventory shows it: a hop's brands are its lots', so it lists those on hand by their
 * short names, "YCH, CF" (Scott, 2026-10-02); anything else has its maker.
 */
export const makerText = (item: Pick<Item, "category" | "manufacturer" | "onHand">): string =>
  item.category === "hop" ? item.onHand.brands.join(", ") : (item.manufacturer ?? "");

/** Where it is from: a hop lists the regions of its lots on hand, anything else its own origin. */
export const regionText = (item: Pick<Item, "category" | "origin" | "onHand">): string =>
  item.category === "hop" ? item.onHand.regions.join(", ") : (regionOf(item) ?? "");

/**
 * What tells a hop from another of the same variety, shown beside its name and never in it (Scott's
 * Jam, 2026-10-02): its form and brand, "T-90 · Yakima Chief Hops". Blank for anything else, which has
 * its maker in front of its name already.
 */
export function hopDetail(item: Pick<Item, "category" | "hopForm" | "manufacturer">): string {
  if (readsWithMaker(item.category, item.hopForm)) return "";
  return [item.hopForm ? HOP_FORM_LABELS[item.hopForm] : null, item.manufacturer]
    .filter(Boolean)
    .join(" · ");
}

/** An item in one line of plain text, as a dropdown shows it: "Citra · T-90 · Yakima Chief Hops". */
export function itemChoice(
  item: Pick<Item, "displayName" | "category" | "hopForm" | "manufacturer">,
): string {
  return [item.displayName, hopDetail(item)].filter(Boolean).join(" · ");
}

/**
 * A pack said the way the known pack of its size is — "55 lb bags" for one received at 25 kg — or by
 * its own size where nothing fits. `options` are the packs for the item's kind (`usePacks`).
 */
export function packPhrase(
  pack: Pack,
  options: readonly PackOption[],
  displayUnit: UnitId,
  count = "1",
): string {
  const known = options.find((o) => o.name === pack.name && sameSize(pack.size, o.size));
  const says = known?.says ?? formatQuantity(pack.size, { unit: displayUnit });
  return `${says} ${plural(pack.name, count)}`;
}

interface Measured {
  displayUnit: UnitId;
  pack: Pack | null;
}

/** One quantity in the item's unit, or in packs when asked and the item has a pack. */
export function showQuantity(item: Measured, canonical: string, mode: "unit" | "pack"): string {
  if (mode === "pack" && item.pack) return formatQuantity(canonical, { pack: item.pack });
  return formatQuantity(canonical, { unit: item.displayUnit });
}

/**
 * What the planned brews leave of an item. Below zero it is how far short, as a brewer says it, not a
 * negative amount: "251 lb short" (Tyler, 2026-10-07), where it read "-251 lb available". `said` follows
 * an amount that is not short, such as " available".
 */
export function showAvailable(
  item: Measured,
  canonical: string,
  mode: "unit" | "pack",
  said = "",
): string {
  const q = dec(canonical);
  return q.lt(ZERO)
    ? `${showQuantity(item, q.abs().toFixed(), mode)} short`
    : `${showQuantity(item, canonical, mode)}${said}`;
}

/**
 * The item's total. In pack mode it uses the server's per-lot pack count, because lots of one item can
 * come in different bag sizes; stock outside any pack is added in the item's unit.
 */
export function showOnHand(item: Item, mode: "unit" | "pack"): string {
  if (mode === "pack" && item.pack) {
    const name = item.pack.name;
    const packs = formatQuantity(item.onHand.packs, { pack: { name, size: "1" } });
    const loose = dec(item.onHand.loose);
    return loose.eq(ZERO)
      ? packs
      : `${packs} + ${formatQuantity(item.onHand.loose, { unit: item.displayUnit })}`;
  }
  return formatQuantity(item.onHand.quantity, { unit: item.displayUnit });
}

/** One location's stock, in packs from the server's per-lot count when asked. */
export function showBalance(
  item: Item,
  balance: { quantity: string; packs: string; loose: string },
  mode: "unit" | "pack",
): string {
  if (mode === "pack" && item.pack) {
    const packs = formatQuantity(balance.packs, { pack: { name: item.pack.name, size: "1" } });
    return dec(balance.loose).eq(ZERO)
      ? packs
      : `${packs} + ${formatQuantity(balance.loose, { unit: item.displayUnit })}`;
  }
  return formatQuantity(balance.quantity, { unit: item.displayUnit });
}

/** Average or derived cost per canonical unit, shown per bag or per unit to match the quantity mode. */
export function showUnitCost(item: Item, perCanonical: string, mode: "unit" | "pack"): string {
  if (mode === "pack" && item.pack) return formatUnitCost(perCanonical, { pack: item.pack });
  return formatUnitCost(perCanonical, { unit: item.displayUnit });
}

/** A price as the invoice stated it: "$32.50/bag" or "$0.742/lb". */
export function showPrice(price: { amount: string; per: string }, packName: string | null): string {
  if (price.per === "pack") return `${formatMoney(price.amount)}/${packName ?? "pack"}`;
  return `${formatMoney(price.amount, 3)}/${unitLabel(price.per)}`;
}

/**
 * Where a spec's value came from. A catalog source names itself and its date ("CMG digital catalog,
 * June 2026"); a person's shows when they set it, as Tyler asked: "User input 09/20/26".
 */
export function specSourceText(source: { name: string; asOf: string } | null): string {
  if (!source) return "";
  if (source.name === USER_SOURCE) return `${source.name} input ${shortDate(source.asOf)}`;
  return source.name;
}

/**
 * The source named once above the spec cards: the reference most of them came from. Values a person
 * set are never the headline, so their "User input 09/20/26" always shows on the spec itself, which is
 * the point of the note (Tyler, 2026-09-20). A set of specs that are all hand-entered says so instead.
 */
export function mainSourceOf(specs: Spec[]): string | null {
  const counts = new Map<string, number>();
  for (const s of specs) {
    if (!s.source || s.source.name === USER_SOURCE) continue;
    counts.set(s.source.name, (counts.get(s.source.name) ?? 0) + 1);
  }
  let best: string | null = null;
  for (const [name, count] of counts) {
    if (best === null || count > (counts.get(best) ?? 0)) best = name;
  }
  if (best === null && specs.some((s) => s.source?.name === USER_SOURCE)) return USER_SOURCE;
  return best;
}

export function shortDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(d.getMonth() + 1)}/${two(d.getDate())}/${two(d.getFullYear() % 100)}`;
}

export function specValue(spec: Spec): SpecValue {
  return spec.kind === "text"
    ? { kind: "text", text: spec.text ?? "" }
    : {
        kind: "numeric",
        qualifier: spec.qualifier ?? "eq",
        min: spec.min,
        max: spec.max,
        unit: spec.unit ?? "percent",
      };
}

export function showSpec(spec: Spec, colorUnit: ColorUnit) {
  return formatSpec(specValue(spec), colorUnit);
}

export function findSpec(specs: Spec[], property: string): Spec | undefined {
  return specs.find((s) => s.property === property);
}

/** "Received", "Borrowed from Keg Creek", "Paid back to Keg Creek", and so on. */
export function movementLabel(m: Movement): string {
  const positive = !m.quantity.startsWith("-");
  if (m.reversesId) return `Undo: ${MOVEMENT_LABELS[m.kind as MovementKind].toLowerCase()}`;
  switch (m.kind) {
    case "borrow":
      return positive ? `Borrowed from ${m.counterparty}` : `Paid back to ${m.counterparty}`;
    case "lend":
      return positive ? `Returned by ${m.counterparty}` : `Lent to ${m.counterparty}`;
    case "transfer":
      return positive ? "Moved in" : "Moved out";
    case "merge":
      return positive ? "Made one item: came in" : "Made one item: went out";
    default:
      return MOVEMENT_LABELS[m.kind as MovementKind];
  }
}

const dateFormat = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const dateYearFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const timeFormat = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

export function showDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return d.getFullYear() === new Date().getFullYear()
    ? dateFormat.format(d)
    : dateYearFormat.format(d);
}

export function showDateTime(iso: string): string {
  const d = new Date(iso);
  return `${showDate(iso)}, ${timeFormat.format(d)}`;
}

export function relative(iso: string | null): string {
  if (!iso) return "Never";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return showDate(iso);
}

export function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A catalog product's name with its maker in front, but never twice: CMG names Franco-Belge's malts
 * "Franco-Belges Special Aromatic Malt" (Tyler, 2026-10-03). The API's own names follow the same rule.
 */
export function withMaker(maker: string | null, name: string): string {
  if (!maker) return name;
  const fold = (t: string) => t.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
  const n = fold(name);
  const m = fold(maker);
  const named = n.startsWith(m) && /^(s|'s|\u2019s)?(\s|$)/u.test(n.slice(m.length));
  return named ? name : `${maker} ${name}`;
}
